// Settings -> Configuration -> 1NCE SIM (documentation/onence.md). The API credential is entered
// write-only: the form is always empty, the secret field is cleared after every attempt, and the
// server only ever answers with status metadata (configured, a client-id hint, replacement time).
// The Management API version (v1 | v2) is one global platform setting used by every SIM lookup and
// the connection test; both versions use the same credential.
import { useEffect, useState } from 'react';
import type { OnenceApiVersion, OnenceView } from '../../../services/onence';
import { Button, Card, LastCheckRow, Rows, StatusBadge } from './ConfigurationViews';

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

const versionText: Record<OnenceApiVersion, string> = {
  v2: 'v2 - 1NCE Platform 2.0 (default)',
  v1: 'v1 - legacy Platform 1.0 SIMs (temporary compatibility)',
};

/** The global Management API version selector. Changing it needs a multi-factor session. */
export function OnenceVersionCard({ view, busy, onSetVersion }: {
  view: OnenceView;
  busy: boolean;
  onSetVersion: (v: OnenceApiVersion) => void;
}) {
  const current = view.api_version;
  const [selected, setSelected] = useState<OnenceApiVersion | ''>(current);
  useEffect(() => setSelected(current), [current]);
  const versions = view.api_versions?.length ? view.api_versions : (['v2', 'v1'] as OnenceApiVersion[]);
  return (
    <Card title="Management API version" status={view.api_version_error ? 'invalid' : undefined}>
      <div data-onence-version-card={current || 'unavailable'} className="space-y-3 max-w-2xl">
        {view.api_version_error && (
          <p className="text-sm text-red-300">The API version setting could not be read; SIM lookups are refused until it can.</p>
        )}
        <fieldset className="space-y-1">
          <legend className="sr-only">1NCE Management API version</legend>
          {[...versions].sort((a, b) => (a === 'v2' ? -1 : b === 'v2' ? 1 : 0)).map((v) => (
            <label key={v} className="flex items-center gap-2 text-sm text-gray-200">
              <input type="radio" name="onence-api-version" value={v} checked={selected === v} onChange={() => setSelected(v)} disabled={busy} />
              <span>{versionText[v] ?? v}</span>
              {v === current && <span className="text-xs text-green-300">(active)</span>}
            </label>
          ))}
        </fieldset>
        <p className="text-xs text-gray-400">
          One global setting for every SIM lookup (Device Details, Assign Radios, Deployment, remote-install preflight) and the connection test. Both versions use the
          same stored credential and token. There is no automatic fallback: SIMs that only the other version can read show as refused (403).
        </p>
        <p className="text-xs text-yellow-200" data-onence-v1-sunset>
          {view.v1_sunset_note || '1NCE retires Management API v1 at the end of 2026; v1 is temporary compatibility for legacy SIMs'}.
        </p>
        <p className="text-xs text-gray-500">
          Changing it needs a multi-factor sign-in and is audited (old and new value). Cached SIM results are discarded;
          run Test connection again afterwards.{view.api_version_updated_at ? ` Last changed ${when(view.api_version_updated_at)}${view.api_version_updated_by ? ` by ${view.api_version_updated_by}` : ''}.` : ''}
        </p>
        <button type="button" disabled={busy || !selected || selected === current} onClick={() => selected && onSetVersion(selected)}
          className="px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50 bg-blue-600 hover:bg-blue-500 text-white">
          Use API {selected || '…'}
        </button>
      </div>
    </Card>
  );
}

export function OnencePanel({ view, busy, onSave, onTest, onRemove, onSetVersion }: {
  view: OnenceView;
  busy: boolean;
  /** Resolves when the server answered; the form clears the secret either way. */
  onSave: (clientId: string, clientSecret: string) => Promise<unknown>;
  onTest: () => void;
  onRemove: () => void;
  onSetVersion: (v: OnenceApiVersion) => void;
}) {
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const c = view.credential;
  const available = c.available;
  const submit = async () => {
    const id = clientId.trim();
    const s = secret;
    setSecret('');
    try {
      await onSave(id, s);
      setClientId('');
    } catch {
      // The area shows the error; the secret stays cleared.
    }
  };

  return (
    <div data-onence-config>
      <Card title={`1NCE SIM provider (Management API ${view.api_version || 'unavailable'})`} status={view.status}
        actions={<Button onClick={onTest} disabled={busy || !c.configured}>{busy ? 'Testing…' : 'Test connection'}</Button>}>
        <Rows rows={[
          ['API', <span key="api" className="font-mono text-xs">{view.base_url} · {view.api_version || '—'} · read-only SIM lookups</span>],
          ['Credential', c.configured
            ? <span key="c"><StatusBadge state="configured" /><span className="ml-2 text-gray-400">client id {c.client_id_hint} · replaced {when(c.updated_at)} · secret never displayed</span></span>
            : <span key="c"><StatusBadge state={available ? (c.error ? 'invalid' : 'not_configured') : 'not_available'} />{c.error && <span className="ml-2 text-gray-400">({c.error})</span>}</span>],
          ['Last connection test', <span key="lc"><LastCheckRow check={view.last_check} />
            <span className="block text-xs text-gray-500">Token request plus one read-only SIM list request with the active version; a test of another version or credential does not count.</span></span>],
          ['Request budget', `${view.rate_budget_tps} requests/s (1NCE default limit: 10/s per customer)`],
          ['1NCE portal', <a key="p" href={view.portal_url} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:underline">{view.portal_url}</a>],
        ]} />
        <p className="mt-4 text-xs text-gray-400">
          A successful token alone does not prove SIM access: the test also lists one SIM, and a 403 there means the credential works but the SIM API of this
          version refuses it. 1NCE data is enrichment only: it never decides a radio's identity, ownership or authentication. Only SIM details and connectivity status
          are read; RadioOS cannot change a SIM.
        </p>
      </Card>

      <OnenceVersionCard view={view} busy={busy} onSetVersion={onSetVersion} />

      <Card title={c.configured ? 'Replace API credential' : 'Set API credential'}>
        {!available ? (
          <p className="text-sm text-yellow-200" data-onence-store-unavailable>
            This deployment has no 1NCE credential store (ONENCE_CREDENTIALS_DIR). The operator tooling creates it on deploy.
          </p>
        ) : (
          <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3 max-w-xl">
            <p className="text-xs text-gray-400">
              Use a dedicated 1NCE Platform 2.0 API credential (Client ID + Client Secret; the 1NCE owner account cannot call the API, and API
              credentials have no per-key permissions). Write-only: the stored secret is never shown and this form never contains it.
            </p>
            <label className="block text-sm">
              <span className="text-gray-300">API Client ID / username</span>
              <input name="onence-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck={false}
                className="mt-1 w-full bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-white font-mono text-xs" />
            </label>
            <label className="block text-sm">
              <span className="text-gray-300">Client Secret / password</span>
              <input name="onence-client-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password"
                spellCheck={false} className="mt-1 w-full bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-white font-mono text-xs" />
            </label>
            <p className="text-xs text-gray-500">Changing the credential needs a multi-factor sign-in. Changes are audited (field names only).</p>
            <div className="flex gap-2">
              <button type="submit" disabled={busy || !clientId.trim() || secret.length < 8}
                className="px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50 bg-blue-600 hover:bg-blue-500 text-white">
                {c.configured ? 'Replace credential' : 'Save credential'}
              </button>
              {c.configured && <Button onClick={onRemove} disabled={busy}>Remove credential</Button>}
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
